import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useMemo, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/use-auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { AdminNav } from "@/components/admin/AdminNav";

type Sub = {
  id: string;
  email: string;
  source: string | null;
  status: string;
  beehiiv_subscription_id: string | null;
  created_at: string;
};

type StatusFilter = "all" | "active" | "unsubscribed" | "pending";

export const Route = createFileRoute("/admin/subscribers")({
  head: () => ({
    meta: [
      { title: "Admin | Subscribers | Scott Cunningham" },
      { name: "description", content: "Subscribers for Scott Cunningham’s website administration." },
      { property: "og:title", content: "Admin | Subscribers | Scott Cunningham" },
      { property: "og:description", content: "Subscribers for Scott Cunningham’s website administration." },
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary" },
      { name: "twitter:title", content: "Admin | Subscribers | Scott Cunningham" },
      { name: "twitter:description", content: "Subscribers for Scott Cunningham’s website administration." },
      { name: "robots", content: "noindex" },
    ],
  }),
  component: SubscribersPage,
});

function SubscribersPage() {
  const { user, isAdmin, loading } = useAuth();
  const navigate = useNavigate();
  const [rows, setRows] = useState<Sub[]>([]);
  const [busy, setBusy] = useState(true);
  const [query, setQuery] = useState("");
  const [status, setStatus] = useState<StatusFilter>("all");

  useEffect(() => {
    if (loading) return;
    if (!user) navigate({ to: "/login" });
  }, [user, loading, navigate]);

  useEffect(() => {
    if (!isAdmin) return;
    setBusy(true);
    supabase
      .from("newsletter_signups")
      .select("id, email, source, status, beehiiv_subscription_id, created_at")
      .order("created_at", { ascending: false })
      .limit(1000)
      .then(({ data }) => {
        setRows((data as Sub[]) ?? []);
        setBusy(false);
      });
  }, [isAdmin]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return rows.filter((r) => {
      if (status !== "all" && r.status !== status) return false;
      if (!q) return true;
      return r.email.toLowerCase().includes(q) || (r.source ?? "").toLowerCase().includes(q);
    });
  }, [rows, query, status]);

  const stats = useMemo(() => {
    const total = rows.length;
    const active = rows.filter((r) => r.status === "active").length;
    const unsub = rows.filter((r) => r.status === "unsubscribed").length;
    const last7 = rows.filter(
      (r) => Date.now() - new Date(r.created_at).getTime() < 7 * 86400000,
    ).length;
    return { total, active, unsub, last7 };
  }, [rows]);

  const exportCsv = () => {
    const header = ["created_at", "email", "status", "source", "beehiiv_subscription_id"];
    const esc = (v: unknown) => {
      if (v == null) return "";
      const s = String(v).replace(/"/g, '""');
      return /[",\n]/.test(s) ? `"${s}"` : s;
    };
    const csv = [header.join(",")]
      .concat(
        filtered.map((r) =>
          [r.created_at, r.email, r.status, r.source, r.beehiiv_subscription_id].map(esc).join(","),
        ),
      )
      .join("\n");
    const blob = new Blob([csv], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `subscribers-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const copyEmails = () =>
    navigator.clipboard
      .writeText(
        filtered
          .filter((r) => r.status === "active")
          .map((r) => r.email)
          .join(", "),
      )
      .catch(() => {});

  if (loading) return <div className="p-12 text-muted-foreground">Loading…</div>;
  if (!user) return null;
  if (!isAdmin) return <div className="p-12 text-muted-foreground">Not authorized.</div>;

  return (
    <div className="mx-auto max-w-6xl px-6 py-16">
      <AdminNav title="Subscribers" />
      <p className="mt-2 text-sm text-muted-foreground">
        Newsletter signups synced from Beehiiv webhooks.
      </p>

      <div className="mt-6 grid grid-cols-2 gap-3 sm:grid-cols-4">
        <Stat label="Total" value={stats.total} />
        <Stat label="Active" value={stats.active} />
        <Stat label="Unsubscribed" value={stats.unsub} />
        <Stat label="Last 7 days" value={stats.last7} />
      </div>

      <div className="mt-6 flex flex-wrap items-center gap-2">
        <Input
          placeholder="Search email or source…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          className="h-9 max-w-xs"
        />
        <select
          value={status}
          onChange={(e) => setStatus(e.target.value as StatusFilter)}
          className="h-9 rounded-md border border-border bg-background px-3 text-sm"
        >
          <option value="all">All statuses</option>
          <option value="active">Active</option>
          <option value="pending">Pending</option>
          <option value="unsubscribed">Unsubscribed</option>
        </select>
        <Button size="sm" variant="outline" onClick={copyEmails}>
          Copy active emails
        </Button>
        <Button size="sm" variant="outline" onClick={exportCsv}>
          Export CSV
        </Button>
        <div className="ml-auto text-xs text-muted-foreground">{filtered.length} shown</div>
      </div>

      <div className="mt-6 overflow-hidden rounded-md border border-border">
        <table className="w-full text-sm">
          <thead className="bg-muted/50 text-left font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
            <tr>
              <th className="px-4 py-3">Joined</th>
              <th className="px-4 py-3">Email</th>
              <th className="px-4 py-3">Status</th>
              <th className="px-4 py-3">Source</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-border">
            {busy && (
              <tr>
                <td colSpan={4} className="px-4 py-6 text-muted-foreground">
                  Loading…
                </td>
              </tr>
            )}
            {!busy && filtered.length === 0 && (
              <tr>
                <td colSpan={4} className="px-4 py-6 text-muted-foreground">
                  No subscribers yet.
                </td>
              </tr>
            )}
            {filtered.map((r) => (
              <tr key={r.id}>
                <td className="px-4 py-3 whitespace-nowrap">
                  {new Date(r.created_at).toLocaleString()}
                </td>
                <td className="px-4 py-3">
                  <a className="hover:underline" href={`mailto:${r.email}`}>
                    {r.email}
                  </a>
                </td>
                <td className="px-4 py-3">
                  <span
                    className={`inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-medium ${
                      r.status === "active"
                        ? "bg-emerald-100 text-emerald-900 dark:bg-emerald-950 dark:text-emerald-200"
                        : r.status === "unsubscribed"
                          ? "bg-muted text-muted-foreground"
                          : "bg-amber-100 text-amber-900 dark:bg-amber-950 dark:text-amber-200"
                    }`}
                  >
                    {r.status}
                  </span>
                </td>
                <td className="px-4 py-3 text-muted-foreground">{r.source ?? "N/A"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Stat({ label, value }: { label: string; value: number }) {
  return (
    <div className="rounded-md border border-border p-3">
      <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-muted-foreground">
        {label}
      </div>
      <div className="mt-1 font-display text-2xl">{value}</div>
    </div>
  );
}
